import { AuthApiError, decodeJwtPayload } from "@onlooker/auth-react";
import type { User } from "../auth";
import type { UserProfile } from "../types/api";
import type { ProjectLabel } from "./labelsApi";
import type { OrgRole } from "./orgsApi";
import {
	AUTH_ENDPOINTS,
	type AuthTokenResponse,
	type RefreshResponse,
} from "./types";

interface MockUser {
	id: string;
	email: string;
	name: string;
	password: string;
}

const MOCK_USERS: Record<string, MockUser> = {
	"test@example.com": {
		id: "user-1",
		email: "test@example.com",
		name: "Test User",
		password: "password123",
	},
};

// email -> every live token for that user, and token -> email (reverse).
//
// Sets rather than single values, because sessions are concurrent. These held
// one token each back when issuing a new pair retired the old one, so "the
// user's token" was unambiguous. It no longer is - a laptop and a phone are
// both live - and anything that has to reach ALL of a user's sessions, like a
// password change, could otherwise only find the most recent one.
const ACCESS_TOKENS = new Map<string, Set<string>>();
const ACCESS_TOKEN_OWNER = new Map<string, string>();
const REFRESH_TOKENS = new Map<string, Set<string>>();
const REFRESH_TOKEN_OWNER = new Map<string, string>();

function trackToken(
	index: Map<string, Set<string>>,
	email: string,
	token: string,
): void {
	const existing = index.get(email);
	if (existing) existing.add(token);
	else index.set(email, new Set([token]));
}
// email -> ISO timestamp of the user's most recent login (WS4 profile).
const LAST_LOGIN = new Map<string, string>();
// Tokens explicitly killed (logout / rotation / session invalidation) so a
// still-unexpired JWT can't be replayed after its owner map entry is gone.
const REVOKED_TOKENS = new Set<string>();

// Short-lived access, long-lived refresh. The values are the seam WS3 tunes
// against their proactive-refresh / expiry-warning lead times: the warning must
// lead by less than the access TTL to be visible, and auto-refresh fires just
// before expiry. Shrink ACCESS_TOKEN_TTL_SECONDS for a faster demo loop.
const ACCESS_TOKEN_TTL_SECONDS = 3 * 60; // 3 minutes — short so WS3's 5-min warning fires at login and auto-refresh fires ~60s pre-expiry, both observable in one demo session
const REFRESH_TOKEN_TTL_SECONDS = 30 * 24 * 60 * 60; // 30 days

let tokenCounter = 0;

function base64UrlEncode(input: string): string {
	const bytes = new TextEncoder().encode(input);
	let base64: string;
	if (typeof btoa === "function") {
		let binary = "";
		for (let i = 0; i < bytes.length; i += 1) {
			binary += String.fromCharCode(bytes[i]);
		}
		base64 = btoa(binary);
	} else {
		const nodeBuffer = (globalThis as { Buffer?: typeof globalThis.Buffer })
			.Buffer;
		base64 = nodeBuffer ? nodeBuffer.from(bytes).toString("base64") : "";
	}
	return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Mint a real (decodable) JWT with an `exp` claim. The signature segment is a
// fixed placeholder — neither the mock nor the client's `decodeJwtPayload`
// verify it; `exp` is what drives refresh scheduling. NOT for production.
function mintJwt(
	email: string,
	type: "access" | "refresh",
	ttlSeconds: number,
	jti: number,
): string {
	const iat = Math.floor(Date.now() / 1000);
	const header = base64UrlEncode(JSON.stringify({ alg: "HS256", typ: "JWT" }));
	const payload = base64UrlEncode(
		JSON.stringify({ sub: email, type, iat, exp: iat + ttlSeconds, jti }),
	);
	return `${header}.${payload}.mock-signature`;
}

// Decode a mock JWT and return its subject email only if it is well-formed, of
// the expected kind, and not expired. Returns undefined otherwise.
function jwtEmailIfValid(
	token: string,
	type: "access" | "refresh",
): string | undefined {
	const payload = decodeJwtPayload(token);
	if (!payload || payload.type !== type) return undefined;
	const exp = typeof payload.exp === "number" ? payload.exp : 0;
	if (Date.now() >= exp * 1000) return undefined;
	return typeof payload.sub === "string" ? payload.sub : undefined;
}

// Resolve the email a bearer access token belongs to. The in-memory owner map
// stays authoritative in-session (so rotation, logout, and email-rename keep
// working); a stateless decode is the fallback when the map is cold — e.g.
// after a page reload — so a valid session survives without server state. Both
// paths honor `exp`, so an aged-out access token 401s and the client refreshes.
function resolveAccessEmail(token: string | undefined): string | undefined {
	if (!token || REVOKED_TOKENS.has(token)) return undefined;
	const decodedEmail = jwtEmailIfValid(token, "access");
	if (!decodedEmail) return undefined; // malformed, wrong kind, or expired
	return ACCESS_TOKEN_OWNER.get(token) ?? decodedEmail;
}

function resolveRefreshEmail(token: string | undefined): string | undefined {
	if (!token || REVOKED_TOKENS.has(token)) return undefined;
	const decodedEmail = jwtEmailIfValid(token, "refresh");
	if (!decodedEmail) return undefined;
	return REFRESH_TOKEN_OWNER.get(token) ?? decodedEmail;
}

function issueTokens(email: string): { token: string; refreshToken: string } {
	tokenCounter += 1;
	const token = mintJwt(
		email,
		"access",
		ACCESS_TOKEN_TTL_SECONDS,
		tokenCounter,
	);
	const refreshToken = mintJwt(
		email,
		"refresh",
		REFRESH_TOKEN_TTL_SECONDS,
		tokenCounter,
	);

	// Issuing a pair no longer retires the user's previous one. It used to, on
	// the reasoning that a superseded token should not be replayable - but
	// apps/api has no such rule, so the effect was that signing in on a second
	// device silently ended the first everywhere except production. Sessions are
	// concurrent (SESSION_LIFECYCLE in @onlooker/api-contract).
	//
	// Refresh still rotates, because there the caller hands over the exact token
	// being replaced; that revocation lives in the /auth/refresh branch, which is
	// the only place that knows which session is being renewed.
	//
	// These two maps still track the most recent pair per user, which is what
	// invalidateSessions() reaches for when a password changes.
	trackToken(ACCESS_TOKENS, email, token);
	ACCESS_TOKEN_OWNER.set(token, email);
	trackToken(REFRESH_TOKENS, email, refreshToken);
	REFRESH_TOKEN_OWNER.set(refreshToken, email);

	return { token, refreshToken };
}

function publicUser(user: MockUser): User {
	return { id: user.id, email: user.email, name: user.name };
}

function readBody<T>(options: RequestInit): T {
	return JSON.parse((options.body as string) ?? "{}") as T;
}

// ---------------------------------------------------------------------------
// WS2 account endpoints (profile, password recovery, email verification,
// account deletion). Purely additive over the WS1 auth mock above — these
// handlers share MOCK_USERS and the token maps so a user created via signup is
// immediately usable here. When WS1 swaps to the real API, this section and its
// matching handlers below can be dropped together.
// ---------------------------------------------------------------------------

interface AccountMeta {
	createdAt: string;
	emailVerified: boolean;
}

const ACCOUNT_META = new Map<string, AccountMeta>();
// reset token -> { email, expiresAt } and verification token -> email.
const RESET_TOKENS = new Map<string, { email: string; expiresAt: number }>();
const VERIFY_TOKENS = new Map<string, string>();
let accountTokenCounter = 0;

const RESET_VERIFY_PREFIX = "/auth/reset-password/verify";

function accountMeta(email: string): AccountMeta {
	let meta = ACCOUNT_META.get(email);
	if (!meta) {
		meta = {
			// The pre-seeded test user reads as an established account; anyone who
			// signs up during the session starts unverified as of now.
			createdAt:
				email === "test@example.com"
					? "2026-01-15T00:00:00.000Z"
					: new Date().toISOString(),
			emailVerified: email === "test@example.com",
		};
		ACCOUNT_META.set(email, meta);
	}
	return meta;
}

function bearerToken(options: RequestInit): string | undefined {
	const header = new Headers(options.headers as HeadersInit | undefined);
	return header.get("Authorization")?.replace("Bearer ", "") || undefined;
}

function emailFromAuthHeader(options: RequestInit): string | undefined {
	return resolveAccessEmail(bearerToken(options));
}

function requireAuth(options: RequestInit): { email: string; user: MockUser } {
	const email = emailFromAuthHeader(options);
	const user = email ? MOCK_USERS[email] : undefined;
	if (!email || !user) {
		throw new AuthApiError(401, "unauthorized", "Invalid token");
	}
	return { email, user };
}

function accountUser(user: MockUser) {
	const meta = accountMeta(user.email);
	return {
		id: user.id,
		email: user.email,
		name: user.name,
		createdAt: meta.createdAt,
		emailVerified: meta.emailVerified,
	};
}

/**
 * End every session this user holds, optionally sparing one refresh token.
 *
 * `spareRefreshToken` is how a password change keeps the session that made it
 * while ending the others - the caller has just proved it knows both passwords,
 * so signing it out is noise. apps/api does the same thing in
 * revokeAllSessionsForUserExcept.
 */
function invalidateSessions(email: string, spareRefreshToken?: string): void {
	for (const token of ACCESS_TOKENS.get(email) ?? []) {
		ACCESS_TOKEN_OWNER.delete(token);
		REVOKED_TOKENS.add(token);
	}
	ACCESS_TOKENS.delete(email);

	const spared = new Set<string>();
	for (const token of REFRESH_TOKENS.get(email) ?? []) {
		if (token === spareRefreshToken) {
			spared.add(token);
			continue;
		}
		REFRESH_TOKEN_OWNER.delete(token);
		REVOKED_TOKENS.add(token);
	}
	if (spared.size > 0) REFRESH_TOKENS.set(email, spared);
	else REFRESH_TOKENS.delete(email);
}

// Move a user (and its live tokens + meta) to a new email key. The existing
// access token stays valid, so the caller's session survives an email change.
function renameUserEmail(oldEmail: string, newEmail: string): void {
	const user = MOCK_USERS[oldEmail];
	if (!user) return;
	user.email = newEmail;
	MOCK_USERS[newEmail] = user;
	delete MOCK_USERS[oldEmail];

	const access = ACCESS_TOKENS.get(oldEmail);
	if (access) {
		ACCESS_TOKENS.set(newEmail, access);
		for (const token of access) ACCESS_TOKEN_OWNER.set(token, newEmail);
		ACCESS_TOKENS.delete(oldEmail);
	}
	const refresh = REFRESH_TOKENS.get(oldEmail);
	if (refresh) {
		REFRESH_TOKENS.set(newEmail, refresh);
		for (const token of refresh) REFRESH_TOKEN_OWNER.set(token, newEmail);
		REFRESH_TOKENS.delete(oldEmail);
	}
	const meta = ACCOUNT_META.get(oldEmail);
	if (meta) {
		ACCOUNT_META.set(newEmail, meta);
		ACCOUNT_META.delete(oldEmail);
	}
}

async function mockAccountApi(
	path: string,
	options: RequestInit,
): Promise<Response | null> {
	// GET /auth/reset-password/verify?token=... — is a reset link still usable?
	if (options.method === "GET" && path.startsWith(RESET_VERIFY_PREFIX)) {
		const query = path.includes("?") ? path.slice(path.indexOf("?") + 1) : "";
		const token = new URLSearchParams(query).get("token") ?? "";
		const entry = RESET_TOKENS.get(token);
		const valid = Boolean(entry && entry.expiresAt > Date.now());
		return json({ valid, email: valid ? entry?.email : undefined });
	}

	// POST /auth/forgot-password — issue a reset token; uniform response.
	if (path === "/auth/forgot-password" && options.method === "POST") {
		const { email } = readBody<{ email: string }>(options);
		if (email && MOCK_USERS[email]) {
			accountTokenCounter += 1;
			const token = `mock-reset-${accountTokenCounter}`;
			RESET_TOKENS.set(token, {
				email,
				expiresAt: Date.now() + 60 * 60 * 1000,
			});
			// Dev "mailbox": surface the link so the flow is testable locally.
			console.info(`[mock] password reset link: /reset-password/${token}`);
		}
		// Same response whether or not the address is registered (no enumeration).
		return json({ success: true });
	}

	// POST /auth/reset-password — consume a reset token and set a new password.
	if (path === "/auth/reset-password" && options.method === "POST") {
		const { token, password } = readBody<{ token: string; password: string }>(
			options,
		);
		const entry = token ? RESET_TOKENS.get(token) : undefined;
		if (!entry || entry.expiresAt <= Date.now() || !MOCK_USERS[entry.email]) {
			if (token) RESET_TOKENS.delete(token);
			throw new AuthApiError(
				400,
				"invalid_reset_token",
				"Reset link is invalid or has expired",
			);
		}
		MOCK_USERS[entry.email].password = password;
		RESET_TOKENS.delete(token); // single use
		invalidateSessions(entry.email); // force re-login everywhere
		return json({ success: true });
	}

	// GET /auth/profile — full account profile for the settings page.
	if (path === "/auth/profile" && options.method === "GET") {
		const { user } = requireAuth(options);
		return json({ user: accountUser(user) });
	}

	// PATCH /auth/profile — update name and/or email.
	if (path === "/auth/profile" && options.method === "PATCH") {
		const { email, user } = requireAuth(options);
		const body = readBody<{ name?: string; email?: string }>(options);

		if (typeof body.name === "string" && body.name.trim()) {
			user.name = body.name.trim();
		}

		const nextEmail = typeof body.email === "string" ? body.email.trim() : "";
		if (nextEmail && nextEmail !== email) {
			if (MOCK_USERS[nextEmail]) {
				throw new AuthApiError(
					409,
					"email_taken",
					"That email is already in use",
				);
			}
			renameUserEmail(email, nextEmail);
			accountMeta(nextEmail).emailVerified = false; // re-verify new address
		}

		return json({ user: accountUser(user) });
	}

	// POST /auth/change-password — verify current password, then rotate it.
	if (path === "/auth/change-password" && options.method === "POST") {
		const { email, user } = requireAuth(options);
		const { current_password, new_password, refreshToken } = readBody<{
			current_password: string;
			new_password: string;
			refreshToken?: string;
		}>(options);
		if (user.password !== current_password) {
			throw new AuthApiError(
				401,
				"invalid_password",
				"Current password is incorrect",
			);
		}
		user.password = new_password;

		// Every other session goes. This used to change the password and leave
		// all sessions alone, which was the one place the two implementations
		// disagreed on substance rather than one simply being unbuilt - and the
		// mock disagreed with itself too, since its reset flow already
		// invalidated everything. Someone changing a password is usually acting
		// on the belief the old one is loose.
		invalidateSessions(email, refreshToken);
		return json({ success: true });
	}

	// GET /auth/account/deletion-preview — what deletion would remove.
	//
	// Static on purpose. The real counts come from two queries over `lessons`,
	// and this mock models a single org as a fixture with no lesson store behind
	// it, so the numbers here exist to exercise the panel's states rather than
	// to be right about any account.
	if (
		path === "/auth/account/deletion-preview" &&
		(options.method ?? "GET") === "GET"
	) {
		requireAuth(options);
		return json({
			publicLessons: 2,
			orgsKeeping: [{ id: MOCK_ORG.id, name: MOCK_ORG.name, lessons: 1 }],
		});
	}

	// DELETE /auth/account — remove the user and end all sessions.
	if (path === "/auth/account" && options.method === "DELETE") {
		const { email } = requireAuth(options);
		invalidateSessions(email);
		delete MOCK_USERS[email];
		ACCOUNT_META.delete(email);
		return json({ success: true });
	}

	// POST /auth/resend-verification — issue a fresh verification token.
	if (path === "/auth/resend-verification" && options.method === "POST") {
		const { email } = requireAuth(options);
		accountTokenCounter += 1;
		const token = `mock-verify-${accountTokenCounter}`;
		VERIFY_TOKENS.set(token, email);
		console.info(`[mock] email verification link: /verify-email/${token}`);
		return json({ success: true });
	}

	// POST /auth/verify-email — mark the address verified.
	if (path === "/auth/verify-email" && options.method === "POST") {
		const { token } = readBody<{ token: string }>(options);
		const email = token ? VERIFY_TOKENS.get(token) : undefined;
		if (!email || !MOCK_USERS[email]) {
			throw new AuthApiError(
				400,
				"invalid_verification_token",
				"Verification link is invalid or has expired",
			);
		}
		accountMeta(email).emailVerified = true;
		VERIFY_TOKENS.delete(token);
		return json({ success: true });
	}

	return null; // not an account endpoint — let the auth mock handle it
}

// ---------------------------------------------------------------------------
// Org management. Mirrors apps/api/src/routes/orgs*.ts and shares the account
// mock's token/auth plumbing above; the real routes live behind
// `apps/web/src/api/orgsApi.ts`.
// ---------------------------------------------------------------------------

/** One org, two members, one pending invitation - enough for the page to render. */
const MOCK_ORG = { id: "org-acme", name: "Acme", role: "owner" as const };
const MOCK_ORG_MEMBERS = [
	{
		user_id: "user-ada",
		name: "Ada",
		email: "ada@example.com",
		role: "owner" as const,
		created_at: "2026-10-01T00:00:00.000Z",
	},
	{
		user_id: "user-bob",
		name: "Bob",
		email: "bob@example.com",
		role: "member" as const,
		created_at: "2026-10-02T00:00:00.000Z",
	},
];
const MOCK_ORG_INVITES = [
	{
		id: "invite-1",
		email: "carol@example.com",
		role: "member" as const,
		expires_at: "2026-10-11T00:00:00.000Z",
		created_at: "2026-10-04T00:00:00.000Z",
	},
];

async function mockOrgsApi(
	path: string,
	options: RequestInit,
): Promise<Response | null> {
	if (!path.startsWith("/api/orgs")) return null;

	const method = options.method ?? "GET";

	// Every org route takes `auth: "session"` except the invite verify below,
	// so refuse an unauthenticated caller here rather than in each branch.
	// Without this the contract's anonymous cases fail on `expected 404 to be
	// 401`: an unhandled path falls through to the mock's unknown-endpoint 404.
	// Task 4 added a narrower version of this guard for exactly that reason;
	// this replaces it.
	if (!path.startsWith("/api/orgs/invites/verify")) {
		requireAuth(options);
	}

	// GET /api/orgs/invites/verify?token=... - matched before the parameterized
	// paths below, because this file matches on prefixes rather than on the
	// router's segment rules.
	if (method === "GET" && path.startsWith("/api/orgs/invites/verify")) {
		const query = path.includes("?") ? path.slice(path.indexOf("?") + 1) : "";
		const supplied = new URLSearchParams(query).get("token") ?? "";
		// No token at all is 400 ("token_required"), not { valid: false } - that
		// answer is reserved for a token that was actually presented and turned
		// out unusable. handleVerifyInvite draws the same line: `!token` throws
		// before any lookup runs.
		if (supplied === "") {
			throw new AuthApiError(400, "token_required", "No invitation token");
		}
		if (supplied === "stale") return json({ valid: false });
		return json({
			valid: true,
			org: { id: MOCK_ORG.id, name: MOCK_ORG.name },
			email: "carol@example.com",
			role: "member",
		});
	}

	// Three sentinel tokens, the same convention the verify branch above uses
	// for "stale" - this is the only way a mock with no real invite store can
	// model handleAcceptInvite's three outcomes. "wrong-account" verifies as
	// valid (see the verify branch above) and only fails here, deliberately:
	// the page renders the accept button and learns the mismatch when it is
	// pressed, which is the actual sequence the 403 state exists to cover.
	if (method === "POST" && path === "/api/orgs/invites/accept") {
		const { token: supplied } = readBody<{ token?: string }>(options);

		if (!supplied) {
			throw new AuthApiError(400, "token_required", "No invitation token");
		}
		if (supplied === "stale") {
			throw new AuthApiError(
				400,
				"invalid_invitation",
				"That invitation cannot be used",
			);
		}
		if (supplied === "wrong-account") {
			throw new AuthApiError(
				403,
				"wrong_account",
				"That invitation was sent to carol@example.com. Sign in as that address to accept it.",
			);
		}
		return json({ org_id: MOCK_ORG.id, role: "member" });
	}

	if (path === "/api/orgs" && method === "GET") {
		return json({ orgs: [MOCK_ORG] });
	}

	if (path === "/api/orgs" && method === "POST") {
		const { name } = readBody<{ name: string }>(options);
		return new Response(
			JSON.stringify({ org: { id: "org-new", name, role: "owner" } }),
			{ status: 201, headers: { "Content-Type": "application/json" } },
		);
	}

	// Every branch below matches on a path SUBSTRING (`.endsWith`, `.includes`)
	// rather than the router's segment rules, so an org, member or invite id
	// containing the literal text "members" or "invites" would mis-route.
	// Never true of a real id - every id here is a `crypto.randomUUID()` -
	// but that is a fact about the real system, not about this string match,
	// so it is worth writing down rather than assumed.
	if (path.endsWith("/members") && method === "GET") {
		return json({ members: MOCK_ORG_MEMBERS });
	}

	if (path.endsWith("/invites") && method === "GET") {
		return json({ invites: MOCK_ORG_INVITES });
	}

	// No `created_at` here, deliberately: handleCreateInvite returns `id`,
	// `email`, `role`, `expires_at` and nothing else (it echoes what it just
	// generated, not a row it read back). The list branch above sends
	// `created_at` because listPendingInvites selects it from a real row. A
	// fabricated value here would hide the exact drift orgsApi.ts's
	// `createInvite` return type exists to surface - see the comment there.
	if (path.endsWith("/invites") && method === "POST") {
		const { email, role } = readBody<{ email: string; role: OrgRole }>(options);
		return new Response(
			JSON.stringify({
				invite: {
					id: "invite-new",
					email,
					role,
					expires_at: "2026-10-11T00:00:00.000Z",
				},
			}),
			{ status: 201, headers: { "Content-Type": "application/json" } },
		);
	}

	if (path.includes("/members/") && method === "PATCH") {
		const { role } = readBody<{ role: OrgRole }>(options);
		return json({ member: { user_id: path.split("/members/")[1], role } });
	}

	if (path.includes("/members/") && method === "DELETE") {
		return json({ removed: path.split("/members/")[1] });
	}

	if (path.includes("/invites/") && method === "DELETE") {
		return json({ revoked: path.split("/invites/")[1] });
	}

	// Matches apps/api's handleRetractOrgLesson's shape (routes/orgs-lessons.ts)
	// - { id, seq, status } - unconditionally, the same way the members and
	// invites DELETE branches above answer without checking the id is real.
	// Nothing under apps/web/src calls this route yet; the branch exists so the
	// mock and the contract cannot drift apart the moment something does.
	if (
		method === "POST" &&
		/^\/api\/orgs\/[^/]+\/lessons\/[^/]+\/retract$/.test(path)
	) {
		const lessonId = path.split("/lessons/")[1]?.split("/")[0] ?? "";
		return json({ id: lessonId, seq: 1, status: "retracted" });
	}

	if (method === "PATCH" && /^\/api\/orgs\/[^/]+$/.test(path)) {
		const { name } = readBody<{ name: string }>(options);
		return json({ org: { id: path.split("/").pop(), name } });
	}

	if (method === "DELETE" && /^\/api\/orgs\/[^/]+$/.test(path)) {
		return json({ deleted: path.split("/").pop() });
	}

	return null;
}

function json(body: unknown, status = 200): Response {
	return new Response(JSON.stringify(body), {
		status,
		headers: { "Content-Type": "application/json" },
	});
}

export async function mockAuthApi(
	path: string,
	options: RequestInit,
): Promise<Response> {
	if (path === AUTH_ENDPOINTS.login && options.method === "POST") {
		const { email, password } = readBody<{ email: string; password: string }>(
			options,
		);

		const user = MOCK_USERS[email];
		if (!user || user.password !== password) {
			throw new AuthApiError(
				401,
				"invalid_credentials",
				"Email or password incorrect",
			);
		}

		const { token, refreshToken } = issueTokens(email);
		LAST_LOGIN.set(email, new Date().toISOString());
		const response: AuthTokenResponse = {
			token,
			refreshToken,
			user: publicUser(user),
		};
		return new Response(JSON.stringify(response), { status: 200 });
	}

	if (path === AUTH_ENDPOINTS.signup && options.method === "POST") {
		const { email, password, name } = readBody<{
			email: string;
			password: string;
			name?: string;
		}>(options);

		if (MOCK_USERS[email]) {
			throw new AuthApiError(409, "user_exists", "User already exists");
		}

		const newUser: MockUser = {
			id: `user-${Date.now()}`,
			email,
			name: name || "Anonymous",
			password,
		};
		MOCK_USERS[email] = newUser;

		const { token, refreshToken } = issueTokens(email);
		LAST_LOGIN.set(email, new Date().toISOString());
		const response: AuthTokenResponse = {
			token,
			refreshToken,
			user: publicUser(newUser),
		};
		// 201, matching apps/api. This answered 200 and nothing noticed, because
		// nothing compares the two - see api-contract.test.ts.
		return new Response(JSON.stringify(response), { status: 201 });
	}

	if (path === AUTH_ENDPOINTS.refresh && options.method === "POST") {
		const { refreshToken } = readBody<{ refreshToken?: string }>(options);
		const email = resolveRefreshEmail(refreshToken);

		if (!email || !MOCK_USERS[email]) {
			throw new AuthApiError(
				401,
				"invalid_refresh_token",
				"Refresh token is invalid or expired",
			);
		}

		// Rotation, and the only place it belongs: the caller has named the exact
		// token it is replacing, so retiring that one ends no other session.
		// apps/api does the same - handleRefresh revokes the presented refresh
		// token before storing its replacement.
		if (refreshToken) {
			REVOKED_TOKENS.add(refreshToken);
			REFRESH_TOKEN_OWNER.delete(refreshToken);
		}

		const rotated = issueTokens(email);
		const response: RefreshResponse = {
			token: rotated.token,
			refreshToken: rotated.refreshToken,
		};
		return new Response(JSON.stringify(response), { status: 200 });
	}

	if (path === AUTH_ENDPOINTS.me && options.method === "GET") {
		const email = resolveAccessEmail(bearerToken(options));

		if (!email || !MOCK_USERS[email]) {
			throw new AuthApiError(401, "unauthorized", "Invalid token");
		}

		return new Response(
			JSON.stringify({ user: publicUser(MOCK_USERS[email]) }),
			{
				status: 200,
			},
		);
	}

	if (path === AUTH_ENDPOINTS.logout && options.method === "POST") {
		// Revokes the refresh token in the body and nothing else, matching
		// apps/api. See SESSION_LIFECYCLE in @onlooker/api-contract for why the
		// access token is left alone: it is a stateless JWT there, so no amount of
		// bookkeeping on this side could withdraw one, and a mock that pretends
		// otherwise teaches the app a guarantee production does not offer.
		//
		// This used to revoke the presented access token and call
		// invalidateSessions(), which ended every session that user had. Both were
		// fictions - one about a token the real server cannot reach, the other
		// about devices it was never told to sign out.
		const { refreshToken } = readBody<{ refreshToken?: string }>(options);
		if (refreshToken) {
			REVOKED_TOKENS.add(refreshToken);
			REFRESH_TOKEN_OWNER.delete(refreshToken);
		}
		return new Response(JSON.stringify({ success: true }), { status: 200 });
	}

	const accountResponse = await mockAccountApi(path, options);
	if (accountResponse) return accountResponse;

	throw new AuthApiError(404, "not_found", `Mock endpoint not found: ${path}`);
}

/**
 * A machine as the mock holds it. Mirrors MachineTokenSummary in
 * apps/api/src/db/machine-tokens.ts field for field - everything the browser
 * is allowed to see, which is everything except anything that authenticates.
 */
interface MockMachine {
	id: string;
	name: string;
	created_at: string;
	last_used_at: string | null;
	revoked_at: string | null;
	removed_at: string | null;
	/**
	 * The reported inventory document, held whole so the detail route can
	 * serve it. The list route sends a count derived from it and never the
	 * document itself, matching the split the real API makes.
	 *
	 * Undefined means this machine has never reported, which the page renders
	 * differently from an inventory naming no plugins.
	 */
	inventory?: unknown;
	inventory_at?: string | null;
	/** The org this machine pushes to, or null for a private-only token. */
	org_id: string | null;
}

/**
 * Per-account machines, keyed by email like the rest of the mock's state.
 *
 * The lesson pool above is permanently empty because only a machine-
 * authenticated push can fill it and a browser cannot make one. Machines are
 * the opposite: a browser is the only thing that can mint one, so the mock can
 * model the whole lifecycle - and has to, because the reveal, the "never used"
 * treatment and revoke are otherwise unreachable in development.
 */
const MACHINES = new Map<string, MockMachine[]>();

function machinesOf(email: string): MockMachine[] {
	const existing = MACHINES.get(email);
	if (existing) return existing;
	const fresh: MockMachine[] = [];
	MACHINES.set(email, fresh);
	return fresh;
}

/**
 * Per-account repository names (ONL-189), keyed by email like MACHINES.
 *
 * Unlike the sessions feed, a browser is the only writer here, so the mock
 * can hold real state - which is what lets the rename flow be exercised in
 * development at all.
 */
const LABELS = new Map<string, ProjectLabel[]>();

function labelsOf(email: string): ProjectLabel[] {
	const existing = LABELS.get(email);
	if (existing) return existing;
	const fresh: ProjectLabel[] = [];
	LABELS.set(email, fresh);
	return fresh;
}

/** Mirrors handlePutProjectLabel in apps/api, validation included. */
function putMockLabel(
	email: string,
	options: RequestInit,
): { project_key: string; machine_id: string | null; label: string | null } {
	const body = readBody<{
		project_key?: unknown;
		machine_id?: unknown;
		label?: unknown;
	}>(options);

	const key = body.project_key;
	if (typeof key !== "string" || !/^[0-9a-f]{12}$/.test(key)) {
		throw new AuthApiError(
			400,
			"invalid_project_key",
			"project_key must be 12 lowercase hex characters",
		);
	}
	const machineId = body.machine_id;
	if (machineId !== null && typeof machineId !== "string") {
		throw new AuthApiError(
			400,
			"invalid_machine_id",
			"machine_id must be a machine's id, or null for a remote key",
		);
	}
	if (
		machineId !== null &&
		!machinesOf(email).some((machine) => machine.id === machineId)
	) {
		throw new AuthApiError(404, "not_found", "No such machine");
	}

	const labels = labelsOf(email);
	const index = labels.findIndex(
		(l) => l.project_key === key && l.machine_id === machineId,
	);
	if (body.label === null) {
		if (index >= 0) labels.splice(index, 1);
		return { project_key: key, machine_id: machineId, label: null };
	}

	const raw = body.label;
	if (
		typeof raw !== "string" ||
		/[\p{Cc}\p{Cf}\p{Zl}\p{Zp}\p{Cs}]/u.test(raw) ||
		raw.trim() === "" ||
		new TextEncoder().encode(raw.trim()).length > 200
	) {
		throw new AuthApiError(400, "invalid_label", "That label is not allowed");
	}
	const stored = { project_key: key, machine_id: machineId, label: raw.trim() };
	if (index >= 0) labels[index] = stored;
	else labels.push(stored);
	return stored;
}

/**
 * Plugins named by a reported document, or null when there is nothing to
 * count. Mirrors `pluginCount` in apps/api: null for unreported and for
 * unreadable alike, because zero would be a claim about the machine.
 */
function mockPluginCount(inventory: unknown): number | null {
	const plugins = (inventory as { plugins?: unknown } | undefined)?.plugins;
	return Array.isArray(plugins) ? plugins.length : null;
}

let mockMachineCounter = 0;

/**
 * `onlk_` plus 64 hex characters, the shape createMachineToken mints.
 *
 * Deterministic rather than random on purpose: the mock is not a security
 * boundary, and a predictable value is assertable. The shape still matters -
 * the contract's forbidden list greps for the prefix, so a mock minting a
 * different one would let a leaked token through on the side the gate cannot
 * see. The raw value is returned and then dropped; nothing here retains it.
 */
function mintMockMachineToken(): string {
	mockMachineCounter += 1;
	return `onlk_${mockMachineCounter.toString(16).padStart(64, "0")}`;
}

// ---------------------------------------------------------------------------
// WS4 protected data endpoints backing the authenticated Profile page.
// Additive over the auth + account mocks above and sharing their user + token
// state. /api/dashboard lived here too until onlooker-yfw deleted it.
// ---------------------------------------------------------------------------

export async function mockDataApi(
	path: string,
	options: RequestInit,
): Promise<Response> {
	if (path === "/api/users/me" && (options.method ?? "GET") === "GET") {
		const { email, user } = requireAuth(options);
		const profile: UserProfile = {
			id: user.id,
			email: user.email,
			name: user.name,
			createdAt: accountMeta(email).createdAt,
			lastLoginAt: LAST_LOGIN.get(email) ?? accountMeta(email).createdAt,
		};
		return json(profile);
	}

	// The hosted pool, mocked. The mock has no lessons and no way to acquire
	// any - lessons arrive by machine-authenticated push, which a browser
	// cannot make - so this is permanently the empty-pool case. That is enough
	// for the contract, which pins the envelope shape rather than contents.
	//
	// Matched on the pathname alone. `path` here still carries the search
	// string - toHandlerPath returns `pathname + search`, because the
	// reset-link handler reads its token straight out of it - so an equality
	// check against "/api/lessons" would stop matching the moment the app
	// asked for ?limit= or ?status=, which is every real call it makes.
	const poolPath = path.split("?")[0];

	if (poolPath === "/api/lessons" && (options.method ?? "GET") === "GET") {
		requireAuth(options);

		// Mirrors handleBrowseLessons in apps/api. The mock accepting a query
		// the API rejects is the same class of divergence as the error envelope:
		// it makes a broken request look fine in development.
		const query = new URLSearchParams(path.split("?")[1] ?? "");

		for (const status of query.getAll("status")) {
			if (!["active", "refuted", "superseded", "retracted"].includes(status)) {
				throw new AuthApiError(
					400,
					"invalid_status",
					"status must be one of active, refuted, superseded, retracted",
				);
			}
		}

		const projects = query.getAll("project");
		if (projects.length > 39) {
			throw new AuthApiError(
				400,
				"invalid_project",
				"Ask for at most 39 projects at once",
			);
		}
		if (!projects.every((key) => /^[0-9a-f]{12}$/.test(key))) {
			throw new AuthApiError(400, "invalid_project", "That is not a project");
		}

		// The real cursor is base64 of `<promoted_at>\n<id>`; anything else was
		// not minted here. The mock's pool is always empty, so a well-formed
		// cursor still yields nothing - only the rejection needs to match.
		const cursor = query.get("cursor");
		// `if (cursor)` and not `!== null`: URLSearchParams returns "" for a
		// bare `?cursor=`, and apps/api guards with `if (opts.cursor)`, which
		// treats "" as absent. Rejecting it here would 400 a request production
		// answers 200 - the same divergence this task exists to close, inverted.
		if (cursor) {
			let decoded: string | null = null;
			try {
				decoded = atob(cursor);
			} catch {
				decoded = null;
			}
			// decodeCursor requires BOTH parts non-empty, not merely two of them:
			// `!promotedAt || !id` in apps/api/src/db/lessons.ts. Counting parts
			// alone accepts "\nabc" and "abc\n", which the API rejects - the same
			// under-rejection this task exists to close, pointed at the mock.
			const parts = decoded === null ? [] : decoded.split("\n");
			if (parts.length !== 2 || !parts[0] || !parts[1]) {
				throw new AuthApiError(
					400,
					"invalid_cursor",
					"That cursor was not issued by this server; start from the first page",
				);
			}
		}

		// `owned_ids` is empty for the same reason `lessons` is - the mock pool
		// holds nothing - but it is sent rather than omitted, because the real
		// API always sends it and the detail pane reads it to decide whether
		// to offer a status control at all. `authors` is empty for the same
		// reason: the mock pool has no org rows to attribute. `retractable_in`
		// is empty for the same reason again - there is no org lesson here for
		// anyone to retract. `former_authors` is empty for the same reason - the
		// mock pool has no org lessons with departed authors.
		return json({
			lessons: [],
			cursor: null,
			has_more: false,
			owned_ids: [],
			authors: {},
			retractable_in: {},
			former_authors: [],
		});
	}

	if (poolPath === "/api/activity" && (options.method ?? "GET") === "GET") {
		requireAuth(options);

		// The activity cursor is a bare sequence number, not the
		// (promoted_at, id) pair /api/lessons uses above - see encodeSeqCursor /
		// decodeSeqCursor in apps/api/src/db/lessons.ts. It is base64 of a plain
		// integer, so decoding it is a parse, not a two-part split.
		const query = new URLSearchParams(path.split("?")[1] ?? "");
		const cursor = query.get("cursor");
		// `if (cursor)` and not `!== null`, same reason as /api/lessons above:
		// URLSearchParams returns "" for a bare `?cursor=`, and apps/api treats
		// "" as absent, so rejecting it here would 400 a request production
		// answers 200.
		if (cursor) {
			let seq: number | null = null;
			try {
				seq = Number.parseInt(atob(cursor), 10);
			} catch {
				seq = null;
			}
			if (seq === null || !Number.isFinite(seq)) {
				throw new AuthApiError(
					400,
					"invalid_cursor",
					"That cursor was not issued by this server; start from the first page",
				);
			}
		}

		// The mock has no feed and no way to acquire one - lesson_feed rows are
		// written by the machine-authenticated push path, which a browser
		// cannot reach - so this is permanently the empty-feed case. That is
		// enough for the contract, which pins the envelope shape rather than
		// contents.
		return json({ events: [], cursor: null, has_more: false });
	}

	// GET /api/sessions is the browser's read of what POST /machine/sessions
	// has written - a machine-authenticated push this mock does not implement
	// (sync runs against a real apps/api or not at all, never the browser
	// mock), so there is no path by which this store could ever hold a row.
	// Permanently empty is therefore correct here, the same way the lesson
	// pool and activity feed above are: enough to serve the contract, which
	// pins the envelope shape and not its contents.
	if (poolPath === "/api/sessions" && (options.method ?? "GET") === "GET") {
		requireAuth(options);

		// The real cursor is base64 of `<started_at>\n<machine_id>\n<session_id>`
		// - see encodeSessionsCursor in apps/api/src/db/session-summaries.ts. A
		// three-part split rather than /api/activity's bare integer parse above,
		// because this table has no unique per-user sequence to encode. The
		// mock's page is always empty, so a well-formed cursor still yields
		// nothing; only the rejection needs to match.
		const query = new URLSearchParams(path.split("?")[1] ?? "");

		// Mirrors parseProjectFilters in apps/api/src/routes/sessions.ts, and
		// runs before the cursor is read, as the route's does. The page stays
		// empty, so only the rejection needs to match.
		const projects = query.getAll("project");
		if (projects.length > 40) {
			throw new AuthApiError(
				400,
				"invalid_project",
				"Ask for at most 40 projects at once",
			);
		}
		if (projects.some((value) => !/^(?:[^:]+:)?[0-9a-f]{12}$/.test(value))) {
			throw new AuthApiError(400, "invalid_project", "That is not a project");
		}

		const cursor = query.get("cursor");
		if (cursor) {
			let decoded: string | null = null;
			try {
				decoded = atob(cursor);
			} catch {
				decoded = null;
			}
			const parts = decoded === null ? [] : decoded.split("\n");
			if (parts.length !== 3 || !parts[0] || !parts[1] || !parts[2]) {
				throw new AuthApiError(
					400,
					"invalid_cursor",
					"That cursor was not issued by this server; start from the first page",
				);
			}
		}

		return json({ sessions: [], cursor: null, has_more: false });
	}

	if (
		poolPath.startsWith("/api/lessons/") &&
		poolPath.endsWith("/status") &&
		options.method === "PATCH"
	) {
		requireAuth(options);
		const { status } = JSON.parse(String(options.body ?? "{}")) as {
			status?: unknown;
		};
		if (status !== "active" && status !== "retracted") {
			throw new AuthApiError(
				400,
				"status_not_allowed",
				"A lesson may be retracted or made active again from here. " +
					"'refuted' belongs to the counter-observation that produced it, " +
					"and 'superseded' must name the lesson that replaced it.",
			);
		}
		// The pool is always empty here, so any id is one nobody holds.
		throw new AuthApiError(404, "not_found", "No such lesson");
	}

	if (
		poolPath.startsWith("/api/lessons/") &&
		(options.method ?? "GET") === "GET"
	) {
		requireAuth(options);
		throw new AuthApiError(404, "not_found", "No such lesson");
	}

	if (poolPath === "/api/machines" && (options.method ?? "GET") === "GET") {
		const { email } = requireAuth(options);
		// The count, never the document - the same split handleListMachines
		// makes. Destructuring `inventory` out is what keeps it from riding
		// along, and MACHINE_LIFECYCLE.inventoryDocumentKey fails if it does.
		return json({
			machines: machinesOf(email).map(({ inventory, ...machine }) => ({
				...machine,
				inventory_at: machine.inventory_at ?? null,
				plugin_count: mockPluginCount(inventory),
				// Carried through from the create call below rather than
				// hardcoded, now that minting can bind to an org.
			})),
		});
	}

	if (poolPath === "/api/machines" && options.method === "POST") {
		const { email } = requireAuth(options);
		const body = readBody<{ name?: unknown; org_id?: unknown }>(options);
		// Trimmed before the emptiness check, matching handleCreateMachine.
		// A mock that accepted "   " would let a machine named nothing into
		// the list in development and 400 in production.
		const name = typeof body.name === "string" ? body.name.trim() : "";
		if (!name) {
			throw new AuthApiError(400, "invalid_name", "A machine needs a name");
		}
		// Absent rather than null when the caller sends no org, matching
		// createMachine in machinesApi.ts - the browser omits the key for a
		// private-only mint rather than sending it as null.
		const orgId = typeof body.org_id === "string" ? body.org_id : null;
		// Mirrors handleCreateMachine's membership check: 404, not 403, for an
		// org the account does not belong to - matching orgs/authorize.ts, a
		// non-member must not be able to tell a real org apart from one that
		// does not exist. The mock has only the one org any account belongs
		// to (MOCK_ORG above), so anything else refuses the same way.
		if (orgId !== null && orgId !== MOCK_ORG.id) {
			throw new AuthApiError(404, "not_found", "No such org");
		}

		mockMachineCounter += 1;
		const id = `mock-machine-${mockMachineCounter}`;
		machinesOf(email).push({
			id,
			name,
			created_at: new Date().toISOString(),
			last_used_at: null,
			revoked_at: null,
			removed_at: null,
			org_id: orgId,
		});

		// The raw token appears here and nowhere else, ever - the same promise
		// handleCreateMachine makes. Nothing above stored it.
		return json(
			{ id, name, token: mintMockMachineToken(), org_id: orgId },
			201,
		);
	}

	if (
		poolPath.startsWith("/api/machines/") &&
		poolPath.endsWith("/remove") &&
		options.method === "POST"
	) {
		const { email } = requireAuth(options);
		const id = poolPath.slice("/api/machines/".length, -"/remove".length);
		const machine = machinesOf(email).find(
			(candidate) =>
				candidate.id === id && candidate.revoked_at && !candidate.removed_at,
		);
		// 404 for all of it, matching handleRemoveMachine.
		if (!machine) {
			throw new AuthApiError(404, "not_found", "No such machine");
		}
		machine.removed_at = new Date().toISOString();
		return json({ success: true });
	}

	// Before the DELETE branch below, which matches on the same prefix. A
	// detail path reaching that branch first would route a read into machine
	// revocation.
	if (
		poolPath.startsWith("/api/machines/") &&
		poolPath.endsWith("/inventory") &&
		(options.method ?? "GET") === "GET"
	) {
		const { email } = requireAuth(options);
		const id = poolPath.slice("/api/machines/".length, -"/inventory".length);
		const machine = machinesOf(email).find((m) => m.id === id);
		// 404 for never-reported and not-yours alike, matching
		// handleGetInventory. Telling them apart confirms which ids exist.
		if (!machine?.inventory || !machine.inventory_at) {
			throw new AuthApiError(
				404,
				"not_found",
				"This machine has not reported an inventory",
			);
		}
		return json({
			inventory: machine.inventory,
			inventory_at: machine.inventory_at,
		});
	}

	if (poolPath.startsWith("/api/machines/") && options.method === "DELETE") {
		const { email } = requireAuth(options);
		const id = poolPath.slice("/api/machines/".length);
		const machine = machinesOf(email).find(
			(candidate) => candidate.id === id && !candidate.revoked_at,
		);
		// 404 and not 403, matching handleRevokeMachine. A 403 would confirm
		// the id exists, which is an existence oracle over other users' rows -
		// and because the lookup is scoped to this account, "someone else's"
		// and "never existed" are already indistinguishable here.
		if (!machine) {
			throw new AuthApiError(404, "not_found", "No such machine");
		}
		machine.revoked_at = new Date().toISOString();
		return json({ success: true });
	}

	// The mock holds no sessions (see /api/sessions), so labels are all it can
	// list. They come back with zero activity, as listProjects returns a label
	// whose sessions have passed retention.
	if (poolPath === "/api/projects" && (options.method ?? "GET") === "GET") {
		const { email } = requireAuth(options);
		return json({
			projects: labelsOf(email).map((label) => ({
				project_key: label.project_key,
				basis: label.machine_id === null ? "remote" : "root",
				machine_id: label.machine_id,
				sessions: 0,
				events: 0,
				last_seen_at: null,
			})),
		});
	}

	if (
		poolPath === "/api/project-labels" &&
		(options.method ?? "GET") === "GET"
	) {
		const { email } = requireAuth(options);
		return json({ labels: labelsOf(email) });
	}

	if (poolPath === "/api/project-labels" && options.method === "PUT") {
		const { email } = requireAuth(options);
		return json(putMockLabel(email, options));
	}

	// `path`, not `poolPath`: the invite-verify branch reads its token straight
	// out of the query string, which `poolPath` has already stripped.
	const orgResponse = await mockOrgsApi(path, options);
	if (orgResponse) return orgResponse;

	throw new AuthApiError(404, "not_found", `Mock endpoint not found: ${path}`);
}

function errorResponse(error: unknown): Response {
	if (error instanceof AuthApiError) {
		// Byte-identical to apps/api's errorHandler, including the header. A
		// mock that answers in a different shape than the thing it stands in
		// for is worse than no mock: it makes development pass and production
		// fail. The Content-Type was missing here too.
		return new Response(
			JSON.stringify({
				success: false,
				error: {
					code: error.code,
					message: error.message,
					details: error.details,
				},
			}),
			{
				status: error.status,
				headers: { "Content-Type": "application/json" },
			},
		);
	}
	const message = error instanceof Error ? error.message : String(error);
	throw new Error(message);
}

/**
 * Reduce whatever the client built to the path the handlers match on.
 *
 * Every handler compares bare paths - `path === "/auth/signup"` - but the URL
 * arriving here is absolute whenever VITE_API_BASE_URL is set. Without this the
 * mock silently worked only with an empty base, which happened to be the
 * default, so the failure surfaced as "Mock endpoint not found" naming a URL
 * rather than anything about the base.
 *
 * The search string is kept because handlers read it: the reset-link check
 * pulls its token straight out of this string.
 */
function toHandlerPath(url: string): string {
	try {
		// The base only matters for relative inputs; absolute URLs ignore it.
		const parsed = new URL(url, "http://mock.invalid");
		return parsed.pathname + parsed.search;
	} catch {
		return url;
	}
}

export function createMockFetch() {
	return async (url: string, options: RequestInit = {}) => {
		const path = toHandlerPath(url);

		if (path.startsWith("/auth/")) {
			try {
				return await mockAuthApi(path, options);
			} catch (error) {
				return errorResponse(error);
			}
		}

		if (path.startsWith("/api/")) {
			try {
				return await mockDataApi(path, options);
			} catch (error) {
				return errorResponse(error);
			}
		}

		// Anything the mock does not own goes to the network as it was given,
		// not as the normalized path.
		return fetch(url, options);
	};
}
